<script setup>
/* Bestehende Spielerfotos nachziehen (nur admin): erzeugt für jedes Foto im
 * Speicher die verkleinerten Fassungen (thumb, md), falls sie fehlen.
 *
 * Läuft im Browser mit den Rechten des angemeldeten Nutzers — kein
 * Serverschlüssel, kein Skript. Je Foto wird das Original einmal geladen; es
 * wird nie verändert. Schon vorhandene Fassungen werden übersprungen, der Lauf
 * lässt sich also beliebig oft wiederholen (z. B. nach neu angelegten Spielern,
 * deren Foto beim Anlegen keine Fassungen bekommt). Ein Fehler bei einem Foto
 * bricht den Lauf nicht ab; Einzelheiten stehen in der Konsole, hier nur Zahlen.
 *
 * Die Fassungen behalten die Transparenz der Originale (WebP, sonst PNG). Fassungen aus der
 * Zeit davor sind JPEG mit weißem Hintergrund (Altbestand). Beim Öffnen wird vorab gezählt
 * (nur lesend), wie viele Fotodateien aktuelle, nur alte oder keine Fassungen haben. Mit dem
 * Haken "Alle kleinen Fassungen neu erzeugen" werden ALLE Fassungen neu geschrieben und
 * dabei vorhandene ersetzt; ohne Haken nur fehlende ergänzt. Originale werden nie
 * verändert, überschrieben oder gelöscht, und auch die alten JPEG-Fassungen werden nicht
 * gelöscht (sie werden nur nicht mehr angezeigt, sobald es eine neuere gibt).
 * Hintergrund und Maße: domain/photoVariants.js.
 */

import { ref, computed, watch } from 'vue';
import OverlayModal from './OverlayModal.vue';
import { usePlayers } from '../composables/usePlayers.js';
import { useVariantBackfill } from '../composables/usePhotoVariants.js';
import { photoTargets } from '../domain/photoVariants.js';

const open = defineModel({ type: Boolean, default: false });

const { players } = usePlayers();
const { running, finished, checking, total, done, created, skipped, failed, report, check, run } =
  useVariantBackfill();

/* "Alle neu erzeugen" (Haken): ersetzt auch vorhandene kleine Fassungen — nötig für die alten
   mit weißem Hintergrund. Die Originale bleiben unangetastet. Der Dialog gibt es nur für admin
   (AccountMenu.vue). */
const recreateAll = ref(false);

/* Beim Öffnen vorab zählen (nur lesend): Der Lauf nennt so vorher, wie viele Bilder betroffen sind. */
watch(open, (isOpen) => {
  if (isOpen) {
    recreateAll.value = false;
    check(players.value);
  }
}, { immediate: true });

/* Fotodateien (Spielerfoto, bei Altbestand zusätzlich das Akten-Foto), nicht Spieler */
const withPhoto = computed(() => players.value.reduce((n, p) => n + photoTargets(p).length, 0));
const percent = computed(() => (total.value ? Math.round((done.value / total.value) * 100) : 0));

function close() {
  if (!running.value) open.value = false;
}
</script>

<template>
  <OverlayModal v-if="open" :title="$t('photoTools.title')" :width="460" @close="close">
    <p class="po-intro">{{ $t('photoTools.intro') }}</p>
    <p class="po-count">{{ $t('photoTools.count', { count: withPhoto }) }}</p>
    <p v-if="checking" class="po-count">{{ $t('photoTools.checking') }}</p>
    <p v-else-if="report" class="po-count" data-testid="po-report">{{ $t('photoTools.report', report) }}</p>

    <label class="po-force">
      <input v-model="recreateAll" type="checkbox" :disabled="running" />
      <span>
        <b>{{ $t('photoTools.forceLabel') }}</b>
        <span v-if="recreateAll" class="po-force-hint">{{ $t('photoTools.forceHint', { count: withPhoto }) }}</span>
      </span>
    </label>

    <div v-if="running || finished" class="po-progress">
      <div class="po-bar" role="progressbar" :aria-valuenow="percent" aria-valuemin="0" aria-valuemax="100">
        <div class="po-bar-fill" :style="{ width: percent + '%' }"></div>
      </div>
      <div class="po-numbers">{{ $t('photoTools.progress', { done, total }) }}</div>
      <ul class="po-result">
        <li>{{ $t('photoTools.created', { count: created }) }}</li>
        <li>{{ $t('photoTools.skipped', { count: skipped }) }}</li>
        <li :class="{ bad: failed }">{{ $t('photoTools.failed', { count: failed }) }}</li>
      </ul>
      <p v-if="finished && failed" class="po-hint">{{ $t('photoTools.failedHint') }}</p>
    </div>

    <template #actions>
      <button class="btn ghost" :disabled="running" @click="close">{{ $t('common.close') }}</button>
      <button class="btn" :disabled="running || !withPhoto" @click="run(players, { force: recreateAll })">
        {{ running ? $t('photoTools.running') : finished ? $t('photoTools.again') : $t('photoTools.start') }}
      </button>
    </template>
  </OverlayModal>
</template>

<style scoped>
.po-intro {
  margin: 0 0 12px;
  color: var(--muted);
  font-size: 0.85rem;
  line-height: 1.5;
}
.po-count {
  margin: 0 0 14px;
  color: var(--text);
  font-size: 0.85rem;
}
.po-force {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 14px;
  font-size: 0.85rem;
  color: var(--text);
  cursor: pointer;
}
.po-force input {
  margin-top: 3px;
}
.po-force-hint {
  display: block;
  margin-top: 4px;
  font-size: 0.78rem;
  color: var(--muted);
}
.po-bar {
  height: 8px;
  border-radius: 4px;
  background: var(--card-hover);
  border: 1px solid var(--border);
  overflow: hidden;
}
.po-bar-fill {
  height: 100%;
  background: var(--green);
  transition: width 0.2s ease;
}
.po-numbers {
  margin-top: 6px;
  font-size: 0.8rem;
  color: var(--muted);
}
.po-result {
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
  font-size: 0.82rem;
  color: var(--text);
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.po-result .bad {
  color: var(--red);
}
.po-hint {
  margin: 10px 0 0;
  font-size: 0.78rem;
  color: var(--muted);
}
</style>
